import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { usePageTracking } from "@/hooks/usePageTracking";
import { usePWATracking } from "@/hooks/usePWATracking";
import { Header } from "@/components/Header";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Physics from "./pages/Physics";
import Chemistry from "./pages/Chemistry";
import Math from "./pages/Math";
import Finance from "./pages/Finance";
import Health from "./pages/Health";
import Lifestyle from "./pages/Lifestyle";
import Other from "./pages/Other";
import Loans from "./pages/Loans";
import Tax from "./pages/Tax";
import SuperCalc from "./pages/SuperCalc";
import Auth from "./pages/Auth";
import Search from "./pages/Search";
import Library from "./pages/Library";
import Sitemap from "./pages/Sitemap";

import Tools from "./pages/Tools";
import Membership from "./pages/Membership";
import Admin from "./pages/Admin";
import Analytics from "./pages/Analytics";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import BlogEditor from "./pages/admin/BlogEditor";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Support from "./pages/Support";
import Install from "./pages/Install";
import LaTeXLearningCenter from "./pages/learn/LaTeXLearningCenter";
import VerifyCertificate from "./pages/VerifyCertificate";
import LaTeXDocumentEditor from "./pages/tools/LaTeXDocumentEditor";

// Physics Calculators
import NewtonsLawCalculator from "./pages/calculators/NewtonsLawCalculator";
import OhmsLawCalculator from "./pages/calculators/OhmsLawCalculator";
import ProjectileMotionCalculator from "./pages/calculators/ProjectileMotionCalculator";
import WorkEnergyCalculator from "./pages/calculators/WorkEnergyCalculator";
import KinematicsCalculator from "./pages/calculators/KinematicsCalculator";
import ForceCalculator from "./pages/calculators/ForceCalculator";
import WorkCalculator from "./pages/calculators/WorkCalculator";
import PowerCalculator from "./pages/calculators/PowerCalculator";
import KineticEnergyCalculator from "./pages/calculators/KineticEnergyCalculator";
import PotentialEnergyCalculator from "./pages/calculators/PotentialEnergyCalculator";
import MomentumCalculator from "./pages/calculators/MomentumCalculator";
import AccelerationCalculator from "./pages/calculators/AccelerationCalculator";
import SpeedVelocityCalculator from "./pages/calculators/SpeedVelocityCalculator";
import FreeFallCalculator from "./pages/calculators/FreeFallCalculator";
import WaveSpeedCalculator from "./pages/calculators/WaveSpeedCalculator";
import FrequencyWavelengthCalculator from "./pages/calculators/FrequencyWavelengthCalculator";
import DopplerEffectCalculator from "./pages/calculators/DopplerEffectCalculator";
import TemperatureConverter from "./pages/calculators/TemperatureConverter";
import HeatTransferCalculator from "./pages/calculators/HeatTransferCalculator";
import ThermalExpansionCalculator from "./pages/calculators/ThermalExpansionCalculator";
import CircuitPowerCalculator from "./pages/calculators/CircuitPowerCalculator";
import ResistorColorCalculator from "./pages/calculators/ResistorColorCalculator";
import ResistanceCalculator from "./pages/calculators/ResistanceCalculator";
import CapacitanceCalculator from "./pages/calculators/CapacitanceCalculator";
import UnitConverter from "./pages/calculators/UnitConverter";
import ScientificNotationConverter from "./pages/calculators/ScientificNotationConverter";
import SignificantFiguresTool from "./pages/calculators/SignificantFiguresTool";
import LaTeXFormatter from "./pages/calculators/LaTeXFormatter";
import RotationalMotionCalculator from "./pages/calculators/RotationalMotionCalculator";
import MomentInertiaCalculator from "./pages/calculators/MomentInertiaCalculator";
import CenterMassCalculator from "./pages/calculators/CenterMassCalculator";
import CoulombsLawCalculator from "./pages/calculators/CoulombsLawCalculator";
import ElectricFieldCalculator from "./pages/calculators/ElectricFieldCalculator";
import MagneticForceCalculator from "./pages/calculators/MagneticForceCalculator";
import FaradaysLawCalculator from "./pages/calculators/FaradaysLawCalculator";
import RLCCircuitCalculator from "./pages/calculators/RLCCircuitCalculator";
import VanDerWaalsCalculator from "./pages/calculators/VanDerWaalsCalculator";
import EntropyChangeCalculator from "./pages/calculators/EntropyChangeCalculator";
import CarnotEfficiencyCalculator from "./pages/calculators/CarnotEfficiencyCalculator";
import TimeDilationCalculator from "./pages/calculators/TimeDilationCalculator";
import LengthContractionCalculator from "./pages/calculators/LengthContractionCalculator";
import RelativisticEnergyCalculator from "./pages/calculators/RelativisticEnergyCalculator";
import DeBroglieCalculator from "./pages/calculators/DeBroglieCalculator";
import PhotoelectricEffectCalculator from "./pages/calculators/PhotoelectricEffectCalculator";
import HeisenbergUncertaintyCalculator from "./pages/calculators/HeisenbergUncertaintyCalculator";
import LensEquationCalculator from "./pages/calculators/LensEquationCalculator";
import MirrorEquationCalculator from "./pages/calculators/MirrorEquationCalculator";
import SnellsLawCalculator from "./pages/calculators/SnellsLawCalculator";
import DiffractionGratingCalculator from "./pages/calculators/DiffractionGratingCalculator";

// Math Calculators
import BasicArithmeticCalculator from "./pages/calculators/BasicArithmeticCalculator";
import PercentageCalculator from "./pages/calculators/PercentageCalculator";
import QuadraticEquationSolver from "./pages/calculators/QuadraticEquationSolver";
import PythagoreanCalculator from "./pages/calculators/PythagoreanCalculator";
import MeanMedianModeCalculator from "./pages/calculators/MeanMedianModeCalculator";
import PermutationCombinationCalculator from "./pages/calculators/PermutationCombinationCalculator";
import StandardDeviationCalculator from "./pages/calculators/StandardDeviationCalculator";
import FractionCalculator from "./pages/calculators/FractionCalculator";
import ExponentRootCalculator from "./pages/calculators/ExponentRootCalculator";
import LinearEquationSolver from "./pages/calculators/LinearEquationSolver";
import AreaPerimeterCalculator from "./pages/calculators/AreaPerimeterCalculator";
import ZScoreCalculator from "./pages/calculators/ZScoreCalculator";
import VolumeCalculator from "./pages/calculators/VolumeCalculator";
import TrigFunctionsCalculator from "./pages/calculators/TrigFunctionsCalculator";
import MatrixCalculator from "./pages/calculators/MatrixCalculator";
import TruthTableCalculator from "./pages/calculators/TruthTableCalculator";
import SetTheoryCalculator from "./pages/calculators/SetTheoryCalculator";
import LogicSimplifierCalculator from "./pages/calculators/LogicSimplifierCalculator";
import MolarityCalculator from "./pages/calculators/MolarityCalculator";
import DilutionCalculator from "./pages/calculators/DilutionCalculator";
import MassToMolesConverter from "./pages/calculators/MassToMolesConverter";
import MolesToMassConverter from "./pages/calculators/MolesToMassConverter";
import PercentCompositionCalculator from "./pages/calculators/PercentCompositionCalculator";
import LimitingReactantCalculator from "./pages/calculators/LimitingReactantCalculator";
import PercentYieldCalculator from "./pages/calculators/PercentYieldCalculator";
import EmpiricalFormulaCalculator from "./pages/calculators/EmpiricalFormulaCalculator";
import MolecularFormulaCalculator from "./pages/calculators/MolecularFormulaCalculator";
import PHCalculator from "./pages/calculators/PHCalculator";
import POHCalculator from "./pages/calculators/POHCalculator";
import HydrogenIonCalculator from "./pages/calculators/HydrogenIonCalculator";
import BufferSolutionCalculator from "./pages/calculators/BufferSolutionCalculator";
import IdealGasLawCalculator from "./pages/calculators/IdealGasLawCalculator";
import BoylesLawCalculator from "./pages/calculators/BoylesLawCalculator";
import CharlesLawCalculator from "./pages/calculators/CharlesLawCalculator";
import CombinedGasLawCalculator from "./pages/calculators/CombinedGasLawCalculator";
import StoichiometryCalculator from "./pages/calculators/StoichiometryCalculator";
import EquilibriumConstantCalculator from "./pages/calculators/EquilibriumConstantCalculator";
import ReactionQuotientCalculator from "./pages/calculators/ReactionQuotientCalculator";
import ICETableSolver from "./pages/calculators/ICETableSolver";
import EnthalpyChangeCalculator from "./pages/calculators/EnthalpyChangeCalculator";
import HessLawCalculator from "./pages/calculators/HessLawCalculator";
import GibbsFreeEnergyCalculator from "./pages/calculators/GibbsFreeEnergyCalculator";
import NernstEquationCalculator from "./pages/calculators/NernstEquationCalculator";
import CellPotentialCalculator from "./pages/calculators/CellPotentialCalculator";
import ElectronConfigurationTool from "./pages/calculators/ElectronConfigurationTool";
import OrbitalDiagramGenerator from "./pages/calculators/OrbitalDiagramGenerator";
import RateLawCalculator from "./pages/calculators/RateLawCalculator";
import HalfLifeCalculator from "./pages/calculators/HalfLifeCalculator";
import ActivationEnergyCalculator from "./pages/calculators/ActivationEnergyCalculator";

// Finance Calculators
import SimpleInterestCalculator from "./pages/calculators/SimpleInterestCalculator";
import CompoundInterestCalculator from "./pages/calculators/CompoundInterestCalculator";
import MortgageCalculator from "./pages/calculators/MortgageCalculator";
import LoanPaymentCalculator from "./pages/calculators/LoanPaymentCalculator";
import LoanAmortizationCalculator from "./pages/calculators/LoanAmortizationCalculator";
import APRCalculator from "./pages/calculators/APRCalculator";
import ROICalculator from "./pages/calculators/ROICalculator";
import PresentValueCalculator from "./pages/calculators/PresentValueCalculator";
import FutureValueCalculator from "./pages/calculators/FutureValueCalculator";
import SavingsCalculator from "./pages/calculators/SavingsCalculator";
import BreakEvenCalculator from "./pages/calculators/BreakEvenCalculator";
import ProfitMarginCalculator from "./pages/calculators/ProfitMarginCalculator";
import DepreciationCalculator from "./pages/calculators/DepreciationCalculator";
import BlackScholesCalculator from "./pages/calculators/BlackScholesCalculator";
import BondYieldCalculator from "./pages/calculators/BondYieldCalculator";
import DurationConvexityCalculator from "./pages/calculators/DurationConvexityCalculator";
import CAPMCalculator from "./pages/calculators/CAPMCalculator";
import SharpeRatioCalculator from "./pages/calculators/SharpeRatioCalculator";
import VaRCalculator from "./pages/calculators/VaRCalculator";
import AnnuityPVCalculator from "./pages/calculators/AnnuityPVCalculator";
import IRRCalculator from "./pages/calculators/IRRCalculator";
import NPVCalculator from "./pages/calculators/NPVCalculator";
import ModifiedDurationCalculator from "./pages/calculators/ModifiedDurationCalculator";
import OptionGreeksCalculator from "./pages/calculators/OptionGreeksCalculator";
import BinomialOptionsCalculator from "./pages/calculators/BinomialOptionsCalculator";
import TimeValueMoneyCalculator from "./pages/calculators/TimeValueMoneyCalculator";
import ActuarialPVCalculator from "./pages/calculators/ActuarialPVCalculator";
import LifeTableCalculator from "./pages/calculators/LifeTableCalculator";
import PortfolioBetaCalculator from "./pages/calculators/PortfolioBetaCalculator";

// General Calculators
import General from "./pages/General";
import BMICalculator from "./pages/calculators/BMICalculator";
import TipCalculator from "./pages/calculators/TipCalculator";
import DateDifferenceCalculator from "./pages/calculators/DateDifferenceCalculator";
import DiscountCalculator from "./pages/calculators/DiscountCalculator";
import BMRCalculator from "./pages/calculators/BMRCalculator";
import BodyFatCalculator from "./pages/calculators/BodyFatCalculator";
import MacroSplitCalculator from "./pages/calculators/MacroSplitCalculator";
import IdealWeightCalculator from "./pages/calculators/IdealWeightCalculator";
import AgeCalculator from "./pages/calculators/AgeCalculator";
import CountdownCalculator from "./pages/calculators/CountdownCalculator";
import TimeZoneConverter from "./pages/calculators/TimeZoneConverter";
import SalesTaxCalculator from "./pages/calculators/SalesTaxCalculator";
import FuelCostCalculator from "./pages/calculators/FuelCostCalculator";
import CookingConverter from "./pages/calculators/CookingConverter";
import CurrencyConverter from "./pages/calculators/CurrencyConverter";
import LoanRepaymentCalculator from "./pages/calculators/LoanRepaymentCalculator";
import SavingsGrowthCalculator from "./pages/calculators/SavingsGrowthCalculator";
import RunningPaceCalculator from "./pages/calculators/RunningPaceCalculator";
import TDEECalculator from "./pages/calculators/TDEECalculator";

const queryClient = new QueryClient();

function AppRoutes() {
  usePageTracking();
  usePWATracking();
  
  return (
    <>
      <Header />
      <Routes>
          <Route path="/" element={<Index />} />
          
          {/* Category Pages */}
          <Route path="/physics" element={<Physics />} />
          <Route path="/chemistry" element={<Chemistry />} />
          <Route path="/math" element={<Math />} />
          <Route path="/finance" element={<Finance />} />
          <Route path="/health" element={<Health />} />
          <Route path="/lifestyle" element={<Lifestyle />} />
          <Route path="/other" element={<Other />} />
          <Route path="/loans" element={<Loans />} />
          <Route path="/tax" element={<Tax />} />
          <Route path="/tools" element={<Tools />} />
          <Route path="/general" element={<General />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/blog/new" element={<BlogEditor />} />
          <Route path="/admin/blog/:id" element={<BlogEditor />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/search" element={<Search />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/learn/latex" element={<LaTeXLearningCenter />} />
          <Route path="/verify/certificate" element={<VerifyCertificate />} />
          <Route path="/tools/latex-editor" element={<LaTeXDocumentEditor />} />
          
          {/* Physics Calculators */}
          <Route path="/calculator/newtons-law" element={<NewtonsLawCalculator />} />
          <Route path="/calculator/ohms-law" element={<OhmsLawCalculator />} />
          <Route path="/calculator/projectile-motion" element={<ProjectileMotionCalculator />} />
          <Route path="/calculator/work-energy" element={<WorkEnergyCalculator />} />
          <Route path="/calculator/kinematics" element={<KinematicsCalculator />} />
          <Route path="/calculator/force" element={<ForceCalculator />} />
          <Route path="/calculator/work" element={<WorkCalculator />} />
          <Route path="/calculator/power" element={<PowerCalculator />} />
          <Route path="/calculator/kinetic-energy" element={<KineticEnergyCalculator />} />
          <Route path="/calculator/potential-energy" element={<PotentialEnergyCalculator />} />
          <Route path="/calculator/momentum" element={<MomentumCalculator />} />
          <Route path="/calculator/acceleration" element={<AccelerationCalculator />} />
          <Route path="/calculator/speed-velocity" element={<SpeedVelocityCalculator />} />
          <Route path="/calculator/free-fall" element={<FreeFallCalculator />} />
          <Route path="/calculator/wave-speed" element={<WaveSpeedCalculator />} />
          <Route path="/calculator/frequency-wavelength" element={<FrequencyWavelengthCalculator />} />
          <Route path="/calculator/doppler-effect" element={<DopplerEffectCalculator />} />
          <Route path="/calculator/temperature-converter" element={<TemperatureConverter />} />
          <Route path="/calculator/heat-transfer" element={<HeatTransferCalculator />} />
          <Route path="/calculator/thermal-expansion" element={<ThermalExpansionCalculator />} />
          <Route path="/calculator/circuit-power" element={<CircuitPowerCalculator />} />
          <Route path="/calculator/resistor-color" element={<ResistorColorCalculator />} />
          <Route path="/calculator/resistance-calculator" element={<ResistanceCalculator />} />
          <Route path="/calculator/capacitance" element={<CapacitanceCalculator />} />
          <Route path="/calculator/unit-converter" element={<UnitConverter />} />
          <Route path="/calculator/scientific-notation" element={<ScientificNotationConverter />} />
          <Route path="/calculator/significant-figures" element={<SignificantFiguresTool />} />
          <Route path="/calculator/latex-formatter" element={<LaTeXFormatter />} />
          <Route path="/calculator/rotational-motion" element={<RotationalMotionCalculator />} />
          <Route path="/calculator/moment-inertia" element={<MomentInertiaCalculator />} />
          <Route path="/calculator/center-mass" element={<CenterMassCalculator />} />
          <Route path="/calculator/coulombs-law" element={<CoulombsLawCalculator />} />
          <Route path="/calculator/electric-field" element={<ElectricFieldCalculator />} />
          <Route path="/calculator/magnetic-force" element={<MagneticForceCalculator />} />
          <Route path="/calculator/faradays-law" element={<FaradaysLawCalculator />} />
          <Route path="/calculator/rlc-circuit" element={<RLCCircuitCalculator />} />
          <Route path="/calculator/van-der-waals" element={<VanDerWaalsCalculator />} />
          <Route path="/calculator/entropy-change" element={<EntropyChangeCalculator />} />
          <Route path="/calculator/carnot-efficiency" element={<CarnotEfficiencyCalculator />} />
          <Route path="/calculator/time-dilation" element={<TimeDilationCalculator />} />
          <Route path="/calculator/length-contraction" element={<LengthContractionCalculator />} />
          <Route path="/calculator/relativistic-energy" element={<RelativisticEnergyCalculator />} />
          <Route path="/calculator/de-broglie" element={<DeBroglieCalculator />} />
          <Route path="/calculator/photoelectric-effect" element={<PhotoelectricEffectCalculator />} />
          <Route path="/calculator/heisenberg-uncertainty" element={<HeisenbergUncertaintyCalculator />} />
          <Route path="/calculator/lens-equation" element={<LensEquationCalculator />} />
          <Route path="/calculator/mirror-equation" element={<MirrorEquationCalculator />} />
          <Route path="/calculator/snells-law" element={<SnellsLawCalculator />} />
          <Route path="/calculator/diffraction-grating" element={<DiffractionGratingCalculator />} />
          
          {/* Chemistry Calculators */}
          <Route path="/calculator/molarity" element={<MolarityCalculator />} />
          <Route path="/calculator/dilution" element={<DilutionCalculator />} />
          <Route path="/calculator/mass-to-moles" element={<MassToMolesConverter />} />
          <Route path="/calculator/moles-to-mass" element={<MolesToMassConverter />} />
          <Route path="/calculator/percent-composition" element={<PercentCompositionCalculator />} />
          <Route path="/calculator/limiting-reactant" element={<LimitingReactantCalculator />} />
          <Route path="/calculator/percent-yield" element={<PercentYieldCalculator />} />
          <Route path="/calculator/empirical-formula" element={<EmpiricalFormulaCalculator />} />
          <Route path="/calculator/molecular-formula" element={<MolecularFormulaCalculator />} />
          <Route path="/calculator/ph" element={<PHCalculator />} />
          <Route path="/calculator/poh" element={<POHCalculator />} />
          <Route path="/calculator/hydrogen-ion" element={<HydrogenIonCalculator />} />
          <Route path="/calculator/buffer-solution" element={<BufferSolutionCalculator />} />
          <Route path="/calculator/ideal-gas-law" element={<IdealGasLawCalculator />} />
          <Route path="/calculator/boyles-law" element={<BoylesLawCalculator />} />
          <Route path="/calculator/charles-law" element={<CharlesLawCalculator />} />
          <Route path="/calculator/combined-gas-law" element={<CombinedGasLawCalculator />} />
          <Route path="/calculator/stoichiometry" element={<StoichiometryCalculator />} />
          <Route path="/calculator/equilibrium-constant" element={<EquilibriumConstantCalculator />} />
          <Route path="/calculator/reaction-quotient" element={<ReactionQuotientCalculator />} />
          <Route path="/calculator/ice-table" element={<ICETableSolver />} />
          <Route path="/calculator/enthalpy-change" element={<EnthalpyChangeCalculator />} />
          <Route path="/calculator/hess-law" element={<HessLawCalculator />} />
          <Route path="/calculator/gibbs-free-energy" element={<GibbsFreeEnergyCalculator />} />
          <Route path="/calculator/nernst-equation" element={<NernstEquationCalculator />} />
          <Route path="/calculator/cell-potential" element={<CellPotentialCalculator />} />
          <Route path="/calculator/electron-configuration" element={<ElectronConfigurationTool />} />
          <Route path="/calculator/orbital-diagram" element={<OrbitalDiagramGenerator />} />
          <Route path="/calculator/rate-law" element={<RateLawCalculator />} />
          <Route path="/calculator/half-life" element={<HalfLifeCalculator />} />
          <Route path="/calculator/activation-energy" element={<ActivationEnergyCalculator />} />
          
          {/* Math Calculators */}
          <Route path="/calculator/basic-arithmetic" element={<BasicArithmeticCalculator />} />
          <Route path="/calculator/percentage" element={<PercentageCalculator />} />
          <Route path="/calculator/fraction" element={<FractionCalculator />} />
          <Route path="/calculator/exponent-root" element={<ExponentRootCalculator />} />
          <Route path="/calculator/linear-equation" element={<LinearEquationSolver />} />
          <Route path="/calculator/quadratic-equation" element={<QuadraticEquationSolver />} />
          <Route path="/calculator/area-perimeter" element={<AreaPerimeterCalculator />} />
          <Route path="/calculator/volume" element={<VolumeCalculator />} />
          <Route path="/calculator/pythagorean" element={<PythagoreanCalculator />} />
          <Route path="/calculator/trig-functions" element={<TrigFunctionsCalculator />} />
          <Route path="/calculator/matrix" element={<MatrixCalculator />} />
          <Route path="/calculator/mean-median-mode" element={<MeanMedianModeCalculator />} />
          <Route path="/calculator/standard-deviation" element={<StandardDeviationCalculator />} />
          <Route path="/calculator/z-score" element={<ZScoreCalculator />} />
          <Route path="/calculator/permutation-combination" element={<PermutationCombinationCalculator />} />
          <Route path="/calculator/truth-table" element={<TruthTableCalculator />} />
          <Route path="/calculator/set-theory" element={<SetTheoryCalculator />} />
          <Route path="/calculator/logic-simplifier" element={<LogicSimplifierCalculator />} />
          
          {/* Finance Calculators */}
          <Route path="/calculator/simple-interest" element={<SimpleInterestCalculator />} />
          <Route path="/calculator/compound-interest" element={<CompoundInterestCalculator />} />
          <Route path="/calculator/mortgage" element={<MortgageCalculator />} />
          <Route path="/calculator/loan-payment" element={<LoanPaymentCalculator />} />
          <Route path="/calculator/loan-amortization" element={<LoanAmortizationCalculator />} />
          <Route path="/calculator/apr" element={<APRCalculator />} />
          <Route path="/calculator/roi" element={<ROICalculator />} />
          <Route path="/calculator/present-value" element={<PresentValueCalculator />} />
          <Route path="/calculator/future-value" element={<FutureValueCalculator />} />
          <Route path="/calculator/savings" element={<SavingsCalculator />} />
          <Route path="/calculator/break-even" element={<BreakEvenCalculator />} />
          <Route path="/calculator/profit-margin" element={<ProfitMarginCalculator />} />
          <Route path="/calculator/depreciation" element={<DepreciationCalculator />} />
          <Route path="/calculator/black-scholes" element={<BlackScholesCalculator />} />
          <Route path="/calculator/bond-yield" element={<BondYieldCalculator />} />
          <Route path="/calculator/duration-convexity" element={<DurationConvexityCalculator />} />
          <Route path="/calculator/capm" element={<CAPMCalculator />} />
          <Route path="/calculator/sharpe-ratio" element={<SharpeRatioCalculator />} />
          <Route path="/calculator/var" element={<VaRCalculator />} />
          <Route path="/calculator/annuity-pv" element={<AnnuityPVCalculator />} />
          <Route path="/calculator/irr" element={<IRRCalculator />} />
          <Route path="/calculator/npv" element={<NPVCalculator />} />
          <Route path="/calculator/modified-duration" element={<ModifiedDurationCalculator />} />
          <Route path="/calculator/option-greeks" element={<OptionGreeksCalculator />} />
          <Route path="/calculator/binomial-options" element={<BinomialOptionsCalculator />} />
          <Route path="/calculator/time-value-money" element={<TimeValueMoneyCalculator />} />
          <Route path="/calculator/actuarial-pv" element={<ActuarialPVCalculator />} />
          <Route path="/calculator/life-table" element={<LifeTableCalculator />} />
          <Route path="/calculator/portfolio-beta" element={<PortfolioBetaCalculator />} />
          
          {/* General Calculators */}
          <Route path="/calculator/bmi" element={<BMICalculator />} />
          <Route path="/calculator/bmr" element={<BMRCalculator />} />
          <Route path="/calculator/body-fat" element={<BodyFatCalculator />} />
          <Route path="/calculator/macro-split" element={<MacroSplitCalculator />} />
          <Route path="/calculator/ideal-weight" element={<IdealWeightCalculator />} />
          <Route path="/calculator/tip" element={<TipCalculator />} />
          <Route path="/calculator/loan-repayment" element={<LoanRepaymentCalculator />} />
          <Route path="/calculator/savings-growth" element={<SavingsGrowthCalculator />} />
          <Route path="/calculator/currency-converter" element={<CurrencyConverter />} />
          <Route path="/calculator/date-difference" element={<DateDifferenceCalculator />} />
          <Route path="/calculator/age" element={<AgeCalculator />} />
          <Route path="/calculator/countdown" element={<CountdownCalculator />} />
          <Route path="/calculator/time-zone" element={<TimeZoneConverter />} />
          <Route path="/calculator/discount" element={<DiscountCalculator />} />
          <Route path="/calculator/sales-tax" element={<SalesTaxCalculator />} />
          <Route path="/calculator/fuel-cost" element={<FuelCostCalculator />} />
          <Route path="/calculator/cooking-converter" element={<CookingConverter />} />
          <Route path="/calculator/running-pace" element={<RunningPaceCalculator />} />
          <Route path="/calculator/tdee" element={<TDEECalculator />} />
          
          {/* SuperCalc */}
          <Route path="/supercalc" element={<SuperCalc />} />
          
          {/* Library */}
          <Route path="/library" element={<Library />} />
          
          {/* Auth */}
          <Route path="/auth" element={<Auth />} />
          
          {/* Legal & Support */}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/install" element={<Install />} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
